// Single source of truth for the Careers page (Careers.vue / pages/careers).
//
// How the rotation works
// ----------------------
// We keep a *pool* of openings below and only ever show a small window of them
// at a time. Which window is shown is derived purely from the calendar date, so
// the roster "shifts around" on its own every few months with no code change and
// no backend — the same date always resolves to the same set, so it's safe for
// SSR / static builds and re-evaluates in the visitor's browser too.
//
// Tuning:
//   ROTATION_MONTHS   how long each window stays up before it advances (3 = quarterly)
//   ROTATION_PATTERN  how MANY openings show in each successive window, as a
//                     repeating cycle. Include a 0 for a deliberately quiet
//                     stretch (no openings), and smaller numbers for lean ones.
// The window also slides forward through the pool as it advances, so consecutive
// hiring waves surface different roles. Add more jobs to the pool for more variety.

export interface Job {
  slug: string;
  title: string;
  // e.g. "Full-time", "Contract"
  type: string;
  // e.g. "Remote", "Remote (EU hours)"
  location: string;
  // Which part of the team — shown as the small label above the title.
  team: string;
  // One-line hook.
  tagline: string;
  // A sentence or two of context.
  description: string;
  // The day-to-day.
  responsibilities: string[];
  // What we're looking for.
  requirements: string[];
}

export const ROTATION_MONTHS = 3;

// How many openings show in each successive window, cycled by rotation period.
// A 0 means a quiet stretch with nothing open; the rest are lean-to-full waves.
// Tweak this freely — it's the single dial for "how busy are we hiring".
export const ROTATION_PATTERN = [2, 0, 1, 2, 0, 3];

// The full pool of openings. Order is not important — the rotation slides a
// window through it. Keep a healthy few more than the largest ROTATION_PATTERN
// value so the visible set actually changes between waves.
export const jobPool: Job[] = [
  {
    slug: 'senior-frontend-engineer',
    title: 'Senior Frontend Engineer',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Own the interfaces our products are judged by.',
    description:
      'You will lead the front-end on client and in-house products — Vue, Nuxt and React apps that need to feel fast and look sharp, from first paint to the last interaction.',
    responsibilities: [
      'Build accessible, component-driven interfaces in Vue / Nuxt and React.',
      'Own performance — Core Web Vitals, bundle size and rendering strategy.',
      'Shape the design-to-code workflow alongside our designers.',
      'Review pull requests and mentor mid-level engineers.',
    ],
    requirements: [
      '4+ years building production front-ends with a modern framework.',
      'Strong TypeScript and a real feel for clean, maintainable component APIs.',
      'An eye for detail — spacing, motion and the small things that read as quality.',
    ],
  },
  {
    slug: 'backend-engineer-go',
    title: 'Backend Engineer (Go)',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Design APIs and services that stay fast under real load.',
    description:
      'You will architect and ship the services that power our platforms — typed, well-tested Go backends where correctness, security and throughput are non-negotiable.',
    responsibilities: [
      'Design and build HTTP/gRPC services and the data models behind them.',
      'Own database schema, migrations and query performance.',
      'Instrument services with logging, metrics and tracing.',
      'Partner with front-end on clean, well-documented API contracts.',
    ],
    requirements: [
      '3+ years writing production Go (or strong Go + another typed backend language).',
      'Solid grasp of relational databases, indexing and transactions.',
      'Comfortable owning a service end to end, from design to on-call.',
    ],
  },
  {
    slug: 'backend-engineer-laravel',
    title: 'Backend Engineer (Laravel)',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Build robust PHP backends teams can trust.',
    description:
      'You will design and ship the Laravel services behind our products — clean, well-tested APIs and data models where reliability, security and developer experience all matter.',
    responsibilities: [
      'Design and build REST APIs, queues and data models in Laravel.',
      'Own Eloquent models, migrations and query performance.',
      'Write feature and unit tests that keep releases safe.',
      'Integrate third-party services, webhooks and background jobs.',
    ],
    requirements: [
      '3+ years building production apps with Laravel and modern PHP.',
      'Strong grasp of relational databases, Eloquent and queues.',
      'Comfortable owning a service from migration to deploy.',
    ],
  },
  {
    slug: 'full-stack-engineer',
    title: 'Full-Stack Engineer',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Ship features end to end — from schema to screen.',
    description:
      'You will move comfortably across the stack — Laravel or Go on the back, Vue / Nuxt on the front — and take features from idea to production without waiting on a hand-off.',
    responsibilities: [
      'Build full features across the back-end API and the front-end UI.',
      'Translate product requirements into pragmatic, shippable solutions.',
      'Write tests that give us the confidence to move quickly.',
      'Keep an eye on the whole flow — performance, UX and data integrity.',
    ],
    requirements: [
      '3+ years of full-stack work with a modern framework on each side.',
      'Confident with relational data, REST APIs and a typed front-end.',
      'A bias for shipping and a healthy respect for maintainability.',
    ],
  },
  {
    slug: 'product-designer',
    title: 'Product Designer (UI/UX)',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Design',
    tagline: 'Turn complex products into interfaces people enjoy.',
    description:
      'You will own the look and feel of the products we build — from early flows and wireframes through to polished, build-ready Figma that our engineers can ship with confidence.',
    responsibilities: [
      'Design end-to-end flows, from user journeys to high-fidelity screens.',
      'Build and maintain design systems and reusable components.',
      'Work side by side with engineers so designs ship as designed.',
      'Prototype and pressure-test ideas before they hit the codebase.',
    ],
    requirements: [
      '3+ years designing digital products, with a portfolio to show for it.',
      'Mastery of Figma and modern design-system practice.',
      'Able to defend decisions with reasoning, not just taste.',
    ],
  },
  {
    slug: 'mobile-engineer',
    title: 'Mobile Engineer (iOS / Android)',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Build native apps that feel effortless on the device.',
    description:
      'You will craft the mobile side of our products — native iOS and Android experiences that are fast, reliable and genuinely pleasant to use every day.',
    responsibilities: [
      'Build and maintain native apps for iOS and/or Android.',
      'Integrate cleanly with our backend APIs and real-time features.',
      'Own the release pipeline through the App Store and Play Store.',
      'Profile and tune for smooth, battery-friendly performance.',
    ],
    requirements: [
      '3+ years shipping native mobile apps (Swift / Kotlin).',
      'Familiar with app-store release processes and platform guidelines.',
      'Care for the details that make an app feel native, not generic.',
    ],
  },
  {
    slug: 'devops-platform-engineer',
    title: 'DevOps / Platform Engineer',
    type: 'Full-time',
    location: 'Remote (EU hours)',
    team: 'Engineering',
    tagline: 'Make shipping safe, fast and boring — in the best way.',
    description:
      'You will own the infrastructure and pipelines our products run on — automating deploys, hardening environments and giving the team a platform they can build on without friction.',
    responsibilities: [
      'Own CI/CD pipelines and infrastructure-as-code.',
      'Manage containerised deploys, monitoring and alerting.',
      'Harden environments and keep secrets and access under control.',
      'Help engineers self-serve with great tooling and clear docs.',
    ],
    requirements: [
      '3+ years in DevOps / platform / SRE-style roles.',
      'Hands-on with Docker, CI/CD and a major cloud provider.',
      'A reliability mindset — you automate the thing instead of repeating it.',
    ],
  },
];

// Map a date to a rotation index. Anchored on the absolute month so the result
// is stable across builds and timezones — the same calendar month always
// resolves to the same window.
function periodIndex(now: Date): number {
  const absoluteMonth = now.getFullYear() * 12 + now.getMonth();
  return Math.floor(absoluteMonth / ROTATION_MONTHS);
}

const PATTERN_SUM = ROTATION_PATTERN.reduce((sum, count) => sum + count, 0);

// The set of openings to show right now — may be empty during a quiet stretch.
// Pure and deterministic: the same date always yields the same jobs. The count
// comes from ROTATION_PATTERN; the window start advances by every opening shown
// so far, so successive waves slide forward through the pool rather than repeat.
export function getActiveJobs(now: Date): Job[] {
  const patternLength = ROTATION_PATTERN.length;
  if (patternLength === 0 || jobPool.length === 0) return [];

  const period = periodIndex(now);
  const slot = ((period % patternLength) + patternLength) % patternLength;
  const count = Math.min(ROTATION_PATTERN[slot], jobPool.length);
  if (count === 0) return [];

  // Openings shown across every full cycle plus the partial run up to this slot.
  const completedCycles = Math.floor(period / patternLength);
  let priorOpenings = completedCycles * PATTERN_SUM;
  for (let i = 0; i < slot; i++) priorOpenings += ROTATION_PATTERN[i];

  const start = ((priorOpenings % jobPool.length) + jobPool.length) % jobPool.length;
  return Array.from({ length: count }, (_, i) => jobPool[(start + i) % jobPool.length]);
}

// First and last day (YYYY-MM-DD) of the rotation window the given date falls in.
// Used to stamp JobPosting structured data with an accurate datePosted (when the
// current wave began) and validThrough (when it rolls over). Formatted from the
// calendar parts directly to stay timezone-safe.
export function getRotationWindow(now: Date): { datePosted: string; validThrough: string } {
  const period = periodIndex(now);
  const startMonth = period * ROTATION_MONTHS;
  const endMonth = startMonth + ROTATION_MONTHS - 1;

  const iso = (absoluteMonth: number, day: number) => {
    const year = Math.floor(absoluteMonth / 12);
    const month = (absoluteMonth % 12) + 1;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  // Day 0 of the month after the window's last month == that last month's final day.
  const lastDay = new Date(Math.floor(endMonth / 12), (endMonth % 12) + 1, 0).getDate();
  return { datePosted: iso(startMonth, 1), validThrough: iso(endMonth, lastDay) };
}
